import { useState, useRef, useEffect } from "react";
import styles from "./UserInfo.module.scss";
import { toast } from "sonner";
import AuthService from "../../../api/services/auth.service";
import Loading from "../../../components/common/loading/Loading";
import TitleUpdater from "../../../components/common/title/TitleUpdater";
import Input from "../../../components/common/input/Input";
import Button from "../../../components/common/button/Button";
import AccountSidebar from "../../../components/common/account-sidebar/AccountSidebar";
import { useTranslation } from "react-i18next";
import { RegisterPayload } from "../../../api/types/auth.types";

const UserInfo = () => {
  const [logo, setLogo] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [originalUserInfo, setOriginalUserInfo] = useState<any>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [loading, setLoading] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [errors, setErrors] = useState<
    Partial<Record<keyof RegisterPayload, string>>
  >({});
  const [userInfo, setUserInfo] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    profile_photo: "",
  });
  const { t } = useTranslation("translation");

  useEffect(() => {
    setLoading(true);
    const getData = async () => {
      try {
        const res = await AuthService.getUserInfo();
        setUserInfo(res.data);
        setOriginalUserInfo(res.data);
        if (res.data.profile_photo) setLogo(res.data.profile_photo);
      } catch (err) {
      } finally {
        setLoading(false);
      }
    };
    getData();
  }, []);

  useEffect(() => {
    if (!originalUserInfo) return;

    const isDifferent =
      JSON.stringify({
        ...userInfo,
        logo,
      }) !==
      JSON.stringify({
        ...originalUserInfo,
        logo: originalUserInfo.profile_photo || null,
      });

    setHasChanges(isDifferent);
  }, [userInfo, logo, originalUserInfo]);

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogo(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    let newErrors: Partial<Record<keyof RegisterPayload, string>> = {};
    if (!userInfo?.first_name?.trim()) {
      newErrors.first_name = t("errors.firstNameRequired") || "";
    }
    if (!userInfo?.last_name?.trim()) {
      newErrors.last_name = t("errors.lastNameRequired") || "";
    }
    if (!userInfo?.phone?.trim()) {
      newErrors.phone = t("errors.phoneRequired") || "";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setUserInfo((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    if (!hasChanges) return;
    setAddLoading(true);
    try {
      const formData = new FormData();
      Object.entries(userInfo).forEach(([key, value]) => {
        if (value !== originalUserInfo?.[key]) {
          formData.append(key, value || "");
        }
      });
      if (logoFile) {
        formData.append("profile_photo", logoFile);
      }
      await AuthService.updateStoreInfo(formData);
      setOriginalUserInfo({ ...userInfo, profile_photo: logo });
      setLogoFile(null);
      setHasChanges(false);
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        t("common_error");
      toast.error(message);
    } finally {
      setAddLoading(false);
    }
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <div className={styles.user_info}>
      <TitleUpdater title={t("user_info")} />
      <div className="container">
        <div className={styles.wrapper}>
          <AccountSidebar />
          <div className={styles.content}>
            <h1 className={styles.pageTitle}>
              {t("account_details") || "Hesab detalları"}
            </h1>
            <form className={styles.form} onSubmit={handleSubmit}>
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleLogoChange}
              />

              <div className={styles.inputs}>
                <Input
                  label={t("firstName") || ""}
                  placeholder={t("placeholders.firstName") || ""}
                  name="first_name"
                  value={userInfo.first_name}
                  onChange={handleChange}
                  error={errors.first_name}
                  className={styles.input}
                />
                <Input
                  label={t("lastName") || ""}
                  placeholder={t("placeholders.lastName") || ""}
                  name="last_name"
                  value={userInfo.last_name}
                  onChange={handleChange}
                  error={errors.last_name}
                  className={styles.input}
                />
                <Input
                  label={t("email") || ""}
                  placeholder={t("placeholders.email") || ""}
                  name="email"
                  value={userInfo.email}
                  onChange={handleChange}
                  disabled
                  className={styles.input}
                />
                <Input
                  label={t("phone") || ""}
                  placeholder={t("placeholders.phone") || ""}
                  name="phone"
                  value={userInfo.phone}
                  onChange={handleChange}
                  phone
                  error={errors.phone}
                  className={styles.input}
                />
                <div className={styles.submit}>
                  <Button type="submit" disabled={!hasChanges || addLoading}>
                    {t("submit")}
                  </Button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserInfo;
